import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from 'react-bootstrap'
import Select from 'react-select'

import { httpOk, POST } from 'helpers/api'
import { formatDate, formatMoney, regexPhoneNumber } from 'helpers/helpers'
import { useSelector } from 'react-redux'

function Return({ showModal, setShowModal, onSuccess }) {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)
	const reduxCashbox = useSelector(state => state.cashbox)

	const [data, setData] = useState({})
	const [returnPaymentTypeId, setReturnPaymentTypeId] = useState(null)
	const [paymentTypeError, setPaymentTypeError] = useState(false)
	// Qaytarish sababi majburiy (2026-08-14, korxona egasining talabi)
	const [noteError, setNoteError] = useState(false)
	const [submitting, setSubmitting] = useState(false) // dublikat qaytarim himoyasi

	// isPrep — tayyor mahsulot (rulon ochib tayyorlangan; dona bilan qaytariladi)
	function isPrep(p) {
		return p?.is_prepared === true || p?.is_prepared === 1 || p?.is_prepared === '1'
	}

	// Har mahsulot turi qaytarishда boshqacha ishlaydi. cfg() bitta qatorning to'liq
	// konfiguratsiyasini beradi: input birligi, ustun qiymatlari, chegara, summa, backendga konvertatsiya.
	//   • Tunikofon (5): kassir КВ kiritadi        → backend metr = Кв / eni
	//   • Profnastil (4): kassir СОНИ (dona) kiritadi → backend metr = dona × Ҳажм
	//   • Aksessuar (3): kassir МЕТР kiritadi       → backend metr = metr
	//   • Produkt (1)/tayyor: kassir СОН (dona)     → backend dona = dona
	// availInput/returnedInput — input birligida (chegara uchun); returnedDisplay — «Қайтарилди» ustuni uchun.
	function cfg(p) {
		const pt = Number(p?.product_type ?? p?.product_type_id)
		const prep = isPrep(p)
		const elq = Number(p?.element_quantity ?? 0)      // Ҳажм — bir dona uzunligi (metr)
		/* Eni — CHIZMA eni katalognikidan ustun (sotuvda ham aynan shunday:
		   ChequeController::applyCheque). Aks holda chizma bilan sotilgan qator
		   boshqa enida qaytarilib, pul ham ombor ham noto'g'ri chiqardi.
		   ⚠️ Eski qatorlarda `draw_width` NULL — ular avvalgidek `width` oladi
		      (butun tarixda 23 363 qatordan 0 tasida draw_width bor). */
		const width = Number(p?.draw_width ?? 0) > 0
			? Number(p?.draw_width)
			: Number(p?.width ?? 0)              // eni
		const qty = Number(p?.quantity ?? 0)              // sotilgan dona
		const metr = Number(p?.metr ?? p?.quantity ?? 0)  // sotilgan metr
		const already = Number(p?.original_returned_quantity ?? 0) // avval qaytarilgan (backend birligi: metr yoki dona)
		const price = Number(p?.sale_price ?? 0)

		// TUNIKOFON — input КВ
		if (!prep && pt === 5 && width > 0 && elq > 0) {
			return {
				unit: t('kv'), roll: true, hajm: elq, avail: qty,
				jami: metr * width, jamiLabel: t('total_kv'),
				returnedDisplay: already * width, availInput: metr * width, returnedInput: already * width,
				amount: v => price * v, toSubmit: v => v / width,
			}
		}
		// PROFNASTIL — input СОНИ (dona); Жами метр = Ҳажм × Сони; qaytarish metr = dona × Ҳажм
		if (!prep && pt === 4) {
			return {
				unit: t('quantity'), roll: true, hajm: elq, avail: qty,
				jami: metr, jamiLabel: t('total_metr'),
				returnedDisplay: already, availInput: qty, returnedInput: elq > 0 ? already / elq : 0,
				amount: v => price * v * elq, toSubmit: v => v * elq,
			}
		}
		// AKSESSUAR — input МЕТР
		if (!prep && pt === 3) {
			return {
				unit: t('metr'), roll: true, hajm: null, avail: metr,
				jami: null, jamiLabel: null,
				returnedDisplay: already, availInput: metr, returnedInput: already,
				amount: v => price * v, toSubmit: v => v,
			}
		}
		// PRODUKT (1) + tayyor — input СОН (dona); Рулон ustuni yo'q
		return {
			unit: t('quantity'), roll: pt !== 1, hajm: null, avail: qty,
			jami: null, jamiLabel: null,
			returnedDisplay: already, availInput: qty, returnedInput: already,
			amount: v => price * v, toSubmit: v => v,
		}
	}

	// Jadval ustunlari chekдаги mahsulot turlaridан kelib chiqadi (bitta tur bo'lsa — aynan o'sha ustunlar).
	const rows = Array.isArray(data?.products) ? data.products : []
	const cfgs = rows.map(cfg)
	const showRoll = cfgs.some(c => c.roll)          // Рулон ustuni (produktда yo'q)
	const showHajm = cfgs.some(c => c.hajm != null)  // Ҳажм ustuni (prof/tunikofon)
	const showJami = cfgs.some(c => c.jami != null)  // Жами метр/кв ustuni (prof/tunikofon)
	const inputUnits = [...new Set(cfgs.map(c => c.unit))]
	const inputLabel = inputUnits.length === 1 ? inputUnits[0] : t('return')   // aralash chekда umumiy sarlavha
	const jamiLabels = [...new Set(cfgs.filter(c => c.jami != null).map(c => c.jamiLabel))]
	const jamiLabel = jamiLabels.length === 1 ? jamiLabels[0] : t('total')
	const footColSpan = 3 + (showRoll ? 1 : 0) + (showHajm ? 1 : 0) + (showJami ? 1 : 0)

	// CHEGIRMA: qaytarilgan summa (totalReturnedAmount) GROSS — chegirmasiz (sale_price × miqdor).
	// Backend esa mijozning HAQIQIY majburiyati NET (total_amount = gross − discount) bo'lgani uchun
	// chegirmani proporsional ayirib NET refund qaytaradi: net = totalReturnedAmount × (total_amount / (total_amount+discount)).
	// (ChequeController.php:3667-3678). Modal ham AYNI NET ni ko'rsatishi shart — aks holda kassir GROSS ni
	// naqd berib kassa kamomadga tushadi.
	const retDiscount = Number(data.discount_amount ?? 0)
	const retGross = Number(data.total_amount ?? 0) + retDiscount
	const discountFactor = (retDiscount > 0 && retGross > 0) ? Number(data.total_amount ?? 0) / retGross : 1
	const netReturnedAmount = Number(data.totalReturnedAmount ?? 0) * discountFactor
	const returnDiscountAmount = Number(data.totalReturnedAmount ?? 0) - netReturnedAmount // ko'rsatiladigan chegirma qismi

	// Naqd qaytarish summasi (qarzdan keyin qolgan) — NET asosida
	// T5: faqat mijoz QARZI (manfiy balans) qaytariladigan summadan ayirilsin; AVANS (musbat balans) naqdga aylanmasin
	const cashToReturn = Math.max(
		netReturnedAmount + Math.min(Number(data.client_balance ?? 0), 0),
		0
	)

	async function createReturn() {
		if (submitting) return // ikki marta bosishda ikkita qaytarim bo'lmasin
		if (cashToReturn > 0 && !returnPaymentTypeId) {
			setPaymentTypeError(true)
			return
		}
		setPaymentTypeError(false)

		/* SABABSIZ QAYTARISH YO'Q (2026-08-14).
		   Backend ham buni tekshiradi (ChequeController::returnProducts validatori) —
		   bu yerdagisi shunchaki xodim serverga borib-kelmasdan darhol ko'rsin uchun.
		   Ikkalasi ham SHART: frontend tekshiruvini chetlab o'tish oson. */
		if (!String(data.note ?? '').trim()) {
			setNoteError(true)
			return
		}
		setNoteError(false)

		setSubmitting(true)

		let dataCopy = structuredClone(data)
		dataCopy.shift_id = reduxAccount.shift?.id

		// Backend har tur uchun o'z birligini kutadi (metr yoki dona). cfg().toSubmit inputni o'sha birlikka aylantiradi:
		// tunikofon Кв→metr (÷eni), profnastil dona→metr (×Ҳажм), aksessuar metr, produkt dona — o'zgarmas.
		for (const p of dataCopy.products || []) {
			if (p.returned_quantity !== '' && p.returned_quantity != null) {
				p.returned_quantity = cfg(p).toSubmit(Number(p.returned_quantity))
			}
		}

		// Tanlangan to'lov turini payments ga qo'shamiz
		if (returnPaymentTypeId) {
			const selectedType = reduxCashbox?.paymentTypes?.find(p => p.id === returnPaymentTypeId)
			dataCopy.payments = [{
				payment_type_id: returnPaymentTypeId,
				payment_type_category_id: selectedType?.payment_type_category_id ?? 1,
				amount: cashToReturn,
			}]
		} else if (!dataCopy.payments?.length) {
			// Qarz kamaytirilsa ham birinchi payment dan olamiz
			dataCopy.payments = [{ payment_type_id: 1, payment_type_category_id: 1, amount: 0 }]
		}

		try {
			const response = await POST('/cheques-products-return', dataCopy, { loader: true, notification: true })
			if (httpOk(response)) {
				if (onSuccess) onSuccess()
				setShowModal(false)
			}
		} finally {
			setSubmitting(false)
		}
	}

	function handleChange(e, index) {
		let dataCopy = { ...data }
		var product = dataCopy.products[index]
		// Bo'sh input ruxsat (tozalash uchun); manfiy/harfli (NaN) va chegaradan katta — bloklanadi.
		// Chegara input birligida: availInput − returnedInput (avval qaytarilgani).
		const c = cfg(product)
		const val = Number(e.target.value)
		const limit = c.availInput - c.returnedInput
		if (e.target.value !== '' && (isNaN(val) || val < 0 || val > limit)) {
			return
		}
		product.returned_quantity = e.target.value
		dataCopy.totalReturnedAmount = 0
		for (let i = 0; i < dataCopy.products.length; i++) {
			const amt = cfg(dataCopy.products[i]).amount(Number(dataCopy.products[i].returned_quantity ?? 0))
			dataCopy.products[i].totalReturnedAmount = amt
			dataCopy.totalReturnedAmount += amt
		}
		setData(dataCopy)
		setPaymentTypeError(false)
	}

	useEffect(() => {
		if (showModal.show) {
			var dataCopy = structuredClone(showModal.cheque)
			dataCopy.payment_type_id = ''
			dataCopy.note = ''
			setData(dataCopy)
			setReturnPaymentTypeId(null)
			setPaymentTypeError(false)
		}
	}, [showModal.show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal
			show={showModal.show} animation={false} centered size="lg"
			onHide={() => setShowModal(false)}>
			<Modal.Header closeButton>
				<h4>
					{t('return')}
				</h4>
			</Modal.Header>
			<Modal.Body>
				<div className="row mb-3">
					<div className="col-6">
						{data.id &&
							<h5>{t('order')} № {data.id}</h5>
						}
						{
							<div className="d-between">
								<div>{t('date')}</div>
								<div>{formatDate(data.cheque_opened_at ? data.cheque_opened_at : new Date(), "dd.MM.yyyy HH:mm")}</div>
							</div>
						}
						<div className="d-between">
							<div>{t('client')}</div>
							<div>{data.client_name}</div>
						</div>
						<div className="d-between">
							<div>{t('phone')}</div>
							<div>{regexPhoneNumber(data.client_phone)}</div>
						</div>
						{data.address &&
							<div className="d-between">
								<div>{t('address')}</div>
								<div>{data.address}</div>
							</div>
						}
						{data.note &&
							<div className="d-between">
								<div>{t('note')}</div>
								<div>{data.note}</div>
							</div>
						}
					</div>

					<div className="col-6">
						<div className="d-flex flex-column justify-content-end h-100">
							<div className="d-between">
								<div>{t('cashier')}</div>
								<div>{data.cashier_name}</div>
							</div>
							<div className="d-between">
								<div>{t('phone')}</div>
								<div>{regexPhoneNumber(data.cashier_phone)}</div>
							</div>
							{data.delivery_type_id &&
								<div className="d-between">
									<div>{t('delivery_type')}</div>
									<div>{data.delivery_type_name}</div>
								</div>
							}
							{data.courier_login &&
								<div className="d-between">
									<div>{t('courier')}</div>
									<div>{data.courier_name}</div>
								</div>
							}
							{data.delivery_date &&
								<div className="d-between">
									<div>{t('delivery_date')}</div>
									<div>{formatDate(data.delivery_date, "dd.MM.yyyy HH:mm")}</div>
								</div>
							}
						</div>
					</div>
				</div>

				<table className="table table-bordered">
					<thead>
						<tr>
							<th style={{ width: '16%' }}>{t('name2')}</th>
							{showRoll && <th style={{ width: '20%' }}>{t('roll')}</th>}
							{showHajm && <th style={{ width: '9%' }} className="text-end">{t('size')}</th>}
							<th style={{ width: '11%' }} className="text-end">{t('available_quantity')}</th>
							{showJami && <th style={{ width: '10%' }} className="text-end">{jamiLabel}</th>}
							<th style={{ width: '10%', background: '#fff3cd', color: '#8a6d3b' }} className="text-end">
								<i className="uil-pen me-1"></i>{inputLabel}
							</th>
							<th style={{ width: '10%' }} className="text-end">{t('returned')}</th>
							<th style={{ width: '14%' }} className="text-end">{t('amount')}</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((item, index) => {
							const c = cfgs[index]
							return (
								<tr key={index}>
									<td>{item.product_name}</td>
									{showRoll && <td>{item.element_name || '—'}</td>}
									{showHajm && <td className="text-end">{c.hajm != null ? formatMoney(c.hajm, 3) : '—'}</td>}
									<td className="text-end">{formatMoney(c.avail, 3)}</td>
									{showJami && <td className="text-end">{c.jami != null ? formatMoney(c.jami, 2) : '—'}</td>}
									<td className="td-input" style={{ background: '#fff8e1' }}>
										<input type="text" placeholder="0" value={item.returned_quantity}
											onChange={(e) => handleChange(e, index)}
											style={{ background: '#fff8e1', border: '2px solid #f0ad4e', borderRadius: 6, fontWeight: 600, textAlign: 'center' }} />
									</td>
									<td className="text-end">{formatMoney(c.returnedDisplay, 3)}</td>
									<td className="text-end">{formatMoney(c.amount(Number(item.returned_quantity ?? 0)))}</td>
								</tr>
							)
						})}
					</tbody>
					<tfoot>
						<tr className="tr-bold">
							<td>{t('total')}</td>
							<td colSpan={footColSpan}></td>
							<td className="text-end">{formatMoney(data.totalReturnedAmount)}</td>
						</tr>
					</tfoot>
				</table>

				<div className="row">
					<div className="col-md-6">
						{/* SABAB MAJBURIY (2026-08-14). Ilgari bu oddiy ixtiyoriy izoh edi
							va bo'sh qoldirilsa ham qaytarish o'tib ketardi — natijada
							hisobotda «nega qaytarilgan» degan savolga javob yo'q edi. */}
						<div className="form-group w-100">
							{/* ⚠️ `t('required')` ISHLATILMADI — u «{{ key }} maydoni majburiy»
								shablonи bo'lib, parametrsiz chaqirilsa ekranda xom `{{ key }}`
								chiqib qoladi. Shu maydon uchun alohida kalit yozildi. */}
							<label htmlFor="note" className={noteError ? 'text-danger' : ''}>
								{t('return_reason')} <span className="text-danger">*</span>
								{noteError && <span className="ms-1">— {t('return_reason_required')}</span>}
							</label>
							<textarea type="text" name="note" style={{ minHeight: '100px' }}
								className={`form-control ${noteError ? 'border border-danger' : ''}`}
								value={data.note}
								onChange={(e) => {
									if (noteError && e.target.value.trim()) setNoteError(false)
									setData({ ...data, note: e.target.value })
								}} />
						</div>
					</div>

					<div className="col-md-5 offset-1">
						<div className="d-flex flex-column justify-content-center h-100">
							{/* Chegirmali chek: qaytariladigan summadan chegirma ulushi proporsional ayiriladi (backend NET qaytaradi) */}
							{returnDiscountAmount > 0 &&
								<div className="d-between fz-16 text-muted">
									<div>{t('discount')}</div>
									<div className="text-end" style={{ width: '130px' }}>
										- {formatMoney(returnDiscountAmount)}
									</div>
								</div>
							}
							{data.client_balance < 0 &&
								<>
									<div className="d-between fz-18 text-dark">
										<div>{t('current_debt')}</div>
										<div className="text-end" style={{ width: '130px' }}>
											{formatMoney(Math.abs(Number(data.client_balance)))}
										</div>
									</div>
									<div className="d-between fz-18 text-dark">
										<div>{t('new_debt')}</div>
										<div className="text-end" style={{ width: '130px' }}>
											{Number(netReturnedAmount) + Number(data.client_balance) > 0 ?
												formatMoney(0)
												:
												formatMoney(Math.abs(Number(data.client_balance) + Number(netReturnedAmount)))
											}
										</div>
									</div>
								</>
							}

							{cashToReturn > 0 &&
								<div className="d-between fz-18 text-dark mb-2">
									<div>{t('to_pay')}</div>
									<div className="text-end fw-bold text-danger" style={{ width: '130px' }}>
										{formatMoney(cashToReturn)}
									</div>
								</div>
							}

							{/* To'lov turi — faqat naqd qaytarish kerak bo'lganda */}
							{cashToReturn > 0 &&
								<div className="form-group mt-2">
									<label className={paymentTypeError ? 'text-danger' : ''}>
										{t('payment_type')} {paymentTypeError && <span>*{t('required')}</span>}
									</label>
									<Select
										options={reduxCashbox?.paymentTypes?.filter(p =>
											p.payment_type_category_id === 1 || p.payment_type_category_id === 2
										)}
										value={reduxCashbox?.paymentTypes?.find(p => p.id === returnPaymentTypeId) || null}
										onChange={(option) => {
											setReturnPaymentTypeId(option?.id ?? null)
											setPaymentTypeError(false)
										}}
										placeholder={t('select')}
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(o) => o.name}
										getOptionValue={(o) => o.id}
										className={paymentTypeError ? 'border border-danger rounded' : ''}
									/>
								</div>
							}

							{data?.payments?.map((item, index) => (
								item.amount > 0 &&
								<div className="d-between gap-2 fz-14 mt-1" key={index}>
									<div>{item.name}:</div>
									<div className="text-end" style={{ width: '140px' }}>
										{formatMoney(item.amount)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<div className="d-flex justify-content-end gap-2">
					<button className="btn btn-danger btn-wide" onClick={() => setShowModal(false)}>
						{t('cancel')}
					</button>
					<button className="btn btn-primary btn-wide" disabled={data?.products?.length === 0 || submitting} onClick={() => createReturn()}>
						{t('return')}
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default Return
